import { Directive, Input, NgZone, ViewChild } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import {
  ControlType,
  DeletedSectionResult,
  DuplicatedSectionResult,
  EditionResult,
  Field,
  FieldDefinitionChangeResult,
  FormComponent,
  FormReturnType,
  NaturalFormConfig,
  NaturalFormFlavour,
  NaturalFormStyle,
  PhotoChange,
  RenamedSectionResult,
  Section,
} from "@cotecna/dynamic-view-engine";
import { Subscription } from "rxjs";
import { bufferTime } from "rxjs/operators";
import { InspectionDetailsBase } from "src/app/inspection/details/inspection-details-base";
import { InspectionCommonData } from "src/app/inspection/model/inspection-common-data";
import { InspectionState } from "src/app/inspection/model/inspection-state";
import { Item } from "src/app/inspection/model/item";
import { InspectionDetailsService } from "src/app/inspection/services/inspection-details.service";

import { ItFieldInfo } from "../../../inspection/model/it-field-info";
import { FormDefinitionService } from "../../../inspection/services/form-definition.service";
import { InspectionFormService } from "../../../inspection/services/inspection-form-service/inspection-form.service";
import { DynamicFormService } from "../../../shared/services/dynamic-form/dynamic-form.service";

@Directive()
export abstract class DynamicFormActionsBase extends InspectionDetailsBase {
  private readonly TIMEDELAY: number = 500;

  private naturalForm: FormComponent;

  @ViewChild("naturalForm")
  public set form(form: FormComponent) {
    this.naturalForm = form;
    if (form) this.initializeFormComponent();
  }

  public get form(): FormComponent {
    return this.naturalForm;
  }

  public formConfig = {
    displayMode: NaturalFormStyle.material,
    flavor: NaturalFormFlavour.informative,
    notifyFieldChange: true,
    returnType: FormReturnType.entity,
    readonly: false,
    allowToRemoveRequiredFields: true,
  } as NaturalFormConfig;

  @Input()
  public item?: Item;

  @Input()
  declare public inspectionId: string;

  @Input()
  public isReadOnly: boolean;

  public sections: Section[] = [];
  public controls: any[] = [];
  public values: ItFieldInfo[] = [];
  public isLoading: boolean = true;

  protected formDataSubscription: Subscription;
  private inspectionCommonDataSubscription: Subscription;
  private fieldChangeSubscription: Subscription;
  private sectionRenamedSubscription: Subscription;
  private sectionDuplicatedSubscription: Subscription;
  private sectionDeletedSubscription: Subscription;
  private fieldRenamedSubscription: Subscription;
  private fieldDuplicatedSubscription: Subscription;
  private fieldDeletedSubscription: Subscription;
  private photoTakenSubscription: Subscription;
  private fullPictureRequestedSubscription: Subscription;
  private onClearedValueSubscription: Subscription;
  protected getFullPictureSubscription: Subscription;

  public constructor(
    public route: ActivatedRoute,
    public formDefinitionService: FormDefinitionService,
    public inspectionFormService: InspectionFormService,
    public inspectionDetailsService: InspectionDetailsService,
    public dynamicFormService: DynamicFormService,
    protected ngZone: NgZone,
  ) {
    super(route);
  }

  protected async initForm(
    sections: Section[],
    controls: any[],
    values: ItFieldInfo[],
  ): Promise<void> {
    const hydratedSections = sections ?? [];
    const hydratedControls = controls ?? [];
    const hydratedValues = values ?? [];

    await this.dynamicFormService.setFormValues(
      hydratedValues,
      hydratedSections,
      hydratedControls,
    );

    this.sections = hydratedSections;
    this.controls = hydratedControls;
    this.values = hydratedValues;
    if (this.form) this.initializeFormComponent();
  }

  private initializeFormComponent(): void {
    this.preparingDependencies(this.sections, this.controls);
    this.setFormValues();
    this.setDateTimeLimits();
    this.subscribeToFormEvents();
    this.isLoading = false;
    this.onFormInitialized();
  }

  protected onFormInitialized(): void {}

  protected resetFormValues() {
    this.sections = [];
    this.controls = [];
    this.values = [];
  }

  protected subscribeToInspectionCommonData(): void {
    this.inspectionCommonDataSubscription =
      this.inspectionDetailsService.inspectionCommonData.subscribe(
        (inspectionCommonData: InspectionCommonData) => {
          this.formConfig.readonly =
            inspectionCommonData?.state == InspectionState.Closed ||
            this.isReadOnly == true;
        },
      );
  }

  protected subscribeToFormEvents() {
    //field events
    const fieldChangesBuffer = this.form?.fieldChange.pipe(
      bufferTime(this.TIMEDELAY),
    );
    this.fieldChangeSubscription = fieldChangesBuffer?.subscribe(
      async (fields) =>
        this.ngZone.run(async () => {
          if (fields.length) {
            const uniqueFields = [
              ...new Set(fields.map((field) => JSON.stringify(field))),
            ].map((field) => JSON.parse(field));
            const accumulatedFieldChanges =
              await this.accumulatingFormFieldChanges(uniqueFields);
            await this.onFormFieldChanges(accumulatedFieldChanges);
          }
        }),
    );

    //form definition events
    this.sectionRenamedSubscription = this.form?.sectionRenamed.subscribe(
      (renamedSection) => this.onSectionRenamed(renamedSection),
    );
    this.sectionDuplicatedSubscription = this.form?.sectionDuplicated.subscribe(
      (duplicatedSection) => this.onSectionDuplicated(duplicatedSection),
    );
    this.sectionDeletedSubscription = this.form?.sectionDeleted.subscribe(
      (deletedSection) => this.onSectionDeleted(deletedSection),
    );
    this.fieldRenamedSubscription = this.form?.fieldRenamed.subscribe(
      (renamedField) => this.onFieldRenamed(renamedField),
    );
    this.fieldDuplicatedSubscription = this.form?.fieldDuplicated.subscribe(
      (duplicatedField) => this.onFieldDuplicated(duplicatedField),
    );
    this.fieldDeletedSubscription = this.form?.fieldDeleted.subscribe(
      (deletedField) => this.onFieldDeleted(deletedField),
    );
    this.onClearedValueSubscription = this.form?.clearedValue.subscribe(
      (editionResult) => this.onClearedValue(editionResult),
    );

    //form photo events
    this.photoTakenSubscription = this.form?.photoTaken.subscribe(
      (photoChange) => this.onPhotoTaken(photoChange),
    );
    this.fullPictureRequestedSubscription =
      this.form?.fullPictureRequested.subscribe((result: string) =>
        this.onFullPictureRequested(result),
      );
  }

  protected unsubscribeToFormEvents() {
    this.formDataSubscription?.unsubscribe();
    this.inspectionCommonDataSubscription?.unsubscribe();
    this.fieldChangeSubscription?.unsubscribe();
    this.sectionRenamedSubscription?.unsubscribe();
    this.sectionDuplicatedSubscription?.unsubscribe();
    this.sectionDeletedSubscription?.unsubscribe();
    this.fieldRenamedSubscription?.unsubscribe();
    this.fieldDuplicatedSubscription?.unsubscribe();
    this.fieldDeletedSubscription?.unsubscribe();
    this.photoTakenSubscription?.unsubscribe();
    this.fullPictureRequestedSubscription?.unsubscribe();
    this.getFullPictureSubscription?.unsubscribe();
    this.onClearedValueSubscription?.unsubscribe();
  }

  protected openPictureTab(result: string) {
    const image = new Image();
    image.src = "data:image/jpg;base64," + result;
    const windowOpened = window.open(globalThis.location.href);
    setTimeout(function () {
      windowOpened.document.title = "Photo";
    }, 0);
    windowOpened.document.write(image.outerHTML);
    windowOpened.document.close();
  }

  protected async setFormValues(): Promise<void> {
    const fieldIdsToSetValues = [
      ...this.getDependencyFieldsIds(),
      ...this.getFormulaFieldsIds(),
    ];
    this.sections.forEach((section) => {
      const fields = section.fields.filter((field) =>
        fieldIdsToSetValues.includes(field.id),
      );
      if (fields?.length) this.form.updateFieldsValues(fields);
    });
  }

  protected async setDateTimeLimits(): Promise<void> {
    const fieldIds: string[] = [];

    this.sections.forEach((section) => {
      const fields = section.fields.filter(
        (field) => field.type === ControlType.DateTime,
      );
      fields.forEach((field) => fieldIds.push(field.id));
    });

    const today: Date = new Date();
    const minDate: Date = new Date(today);
    const maxDate: Date = new Date(today);

    minDate.setFullYear(today.getFullYear() - 50);
    maxDate.setFullYear(today.getFullYear() + 20);

    fieldIds.forEach((id) => {
      const control = this.controls.find((control) => control.id === id);
      if (control) {
        control.minDate = minDate;
        control.maxDate = maxDate;
      }
    });
  }

  protected preparingDependencies(sections: Section[], controls: any[]) {
    sections.forEach((section) => {
      section.fields.forEach((field) => {
        const control = controls.find((c) => c.id === field.id);
        control?.dependencies?.forEach((dependency) => {
          dependency.trigger?.actions.forEach((action) => {
            action.value = this.getActionValue(action.value);
          });
        });
      });
    });
  }

  protected setReadonlyState(): void {
    this.formConfig.readonly = this.isReadOnly ? true : false;
  }

  public abstract onFormFieldChanges(formFieldChanges: FormFieldChange[]);
  public abstract onSectionRenamed(result: RenamedSectionResult);
  public abstract onSectionDuplicated(result: DuplicatedSectionResult);
  public abstract onSectionDeleted(result: DeletedSectionResult);
  public abstract onFieldRenamed(result: FieldDefinitionChangeResult);
  public abstract onFieldDuplicated(result: FieldDefinitionChangeResult);
  public abstract onFieldDeleted(result: FieldDefinitionChangeResult);
  public abstract onFullPictureRequested(fieldId: string);

  private onPhotoTaken(result: PhotoChange) {
    this.sendFullPictureToNetwork(result.field.id, result.photo);
  }

  private async onClearedValue(editionResult: EditionResult) {
    if (editionResult.field.type == ControlType.Photo) {
      await this.sendFullPictureToNetwork(editionResult.field.id, null);
    }
  }

  private async sendFullPictureToNetwork(fieldId: string, photo: string) {
    try {
      await this.inspectionFormService.sendFullPictureToNetwork(
        this.inspectionId,
        this.item?.id ?? "",
        fieldId,
        photo,
      );
    } catch (err) {
      console.error("send full picture to network", err);
      throw err;
    }
  }

  private async accumulatingFormFieldChanges(fieldChanges: Field[]) {
    const accumulatedFieldChanges: FormFieldChange[] = [];
    for (const field of fieldChanges) {
      const fieldChange = this.mapToFieldChange(field);
      if (fieldChange) {
        const index = accumulatedFieldChanges.findIndex(
          (fieldChange) => fieldChange.fieldId === field.id,
        );
        if (index >= 0) accumulatedFieldChanges[index] = fieldChange;
        else accumulatedFieldChanges.push(fieldChange);
      }
    }
    return accumulatedFieldChanges;
  }

  private mapToFieldChange(field: Field): FormFieldChange {
    return {
      inspectionId: this.inspectionId,
      itemId: this.item?.id,
      fieldId: field.id,
      label: field.label,
      controlType: field.type,
      value: field.value,
      timestamp: new Date(),
      propertyName: field.propertyName,
    } as FormFieldChange;
  }

  private getActionValue(value: any): any {
    switch (value) {
      case "false":
      case "0":
        return false;
      case "true":
      case "1":
        return true;
      default:
        return value;
    }
  }

  private getDependencyFieldsIds(): string[] {
    const dependencyFieldsIds = [];
    const controlsIdsWithDependencies = this.form?.controls
      ?.filter((control) => control?.dependencies?.length)
      .map((c) => c.id);
    const controlsWithDependencies = this.form?.controls?.filter((control) =>
      controlsIdsWithDependencies.includes(control.id),
    );

    controlsWithDependencies?.forEach((control) => {
      dependencyFieldsIds.push(
        ...control?.dependencies.map((dependency) => dependency.fieldId),
      );
    });
    return dependencyFieldsIds;
  }

  private getFormulaFieldsIds(): string[] {
    const formulaFieldsIds = [];
    const controlsIdsWithFormulas = this.form?.controls
      ?.filter((control) => control.formulaIds?.length)
      .map((c) => c.id);
    const controlsWithFormulas = this.form?.controls?.filter((control) =>
      controlsIdsWithFormulas.includes(control.id),
    );

    controlsWithFormulas?.forEach((control) => {
      formulaFieldsIds.push(control.id);
    });
    return formulaFieldsIds;
  }
}

export interface FormFieldChange {
  inspectionId: string;
  itemId?: string;
  fieldId: string;
  label: string;
  controlType?: string;
  value: any;
  timestamp: Date;
  propertyName?: string;
}
